import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, RotateCcw } from "lucide-react";

import { tablePrefs, type SortPref } from "../../lib/tablePrefs";

import styles from "./DataTable.module.css";

export type SortValue = string | number | Date | null | undefined;

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string;
  /** Texto plano usado no gestor de colunas. Default: header se for string, senão key. */
  label?: string;
  /** Permite ordenar por esta coluna. Requer `sortValue`. */
  sortable?: boolean;
  /** Função que extrai um valor comparável de cada linha. */
  sortValue?: (row: T) => SortValue;
  /** Permite esconder esta coluna no gestor (default: true). */
  hideable?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  empty?: ReactNode;
  maxHeight?: string;
  /** Quando definido, activa o gestor de colunas e persiste sort/visibilidade em localStorage. */
  storageKey?: string;
}

function compareValues(a: SortValue, b: SortValue): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "pt-PT", {
    numeric: true,
    sensitivity: "base",
  });
}

function columnLabel<T>(col: Column<T>): string {
  if (col.label) return col.label;
  if (typeof col.header === "string") return col.header;
  return col.key;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  maxHeight,
  storageKey,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<SortPref | null>(() =>
    storageKey ? tablePrefs.getSort(storageKey) : null,
  );
  const [hidden, setHidden] = useState<Set<string>>(() =>
    storageKey ? new Set(tablePrefs.getHidden(storageKey)) : new Set(),
  );

  useEffect(() => {
    if (!storageKey) return;
    tablePrefs.setSort(storageKey, sort);
  }, [storageKey, sort]);

  useEffect(() => {
    if (!storageKey) return;
    tablePrefs.setHidden(storageKey, [...hidden]);
  }, [storageKey, hidden]);

  function toggleSort(col: Column<T>) {
    if (!col.sortable || !col.sortValue) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: "asc" };
      if (prev.dir === "asc") return { key: col.key, dir: "desc" };
      return null;
    });
  }

  function toggleColumn(key: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function resetColumns() {
    setHidden(new Set());
  }

  const visibleColumns = columns.filter((c) => !hidden.has(c.key));

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col || !col.sortValue) return rows;
    const valueOf = col.sortValue;
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => compareValues(valueOf(a), valueOf(b)) * sign);
  }, [rows, sort, columns]);

  const showToolbar = Boolean(storageKey);

  return (
    <div className={styles.shell}>
      {showToolbar && (
        <div className={styles.toolbar}>
          <ColumnsPicker
            columns={columns}
            hidden={hidden}
            onToggle={toggleColumn}
            onReset={resetColumns}
          />
        </div>
      )}
      <div
        className={styles.wrap}
        style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}
      >
        <table className={styles.table}>
          <colgroup>
            {visibleColumns.map((c) => (
              <col key={c.key} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map((c) => {
                const isSorted = sort?.key === c.key;
                const sortable = Boolean(c.sortable && c.sortValue);
                return (
                  <th
                    key={c.key}
                    className={`${styles.th} ${sortable ? styles.thSortable : ""} ${
                      isSorted ? styles.thSorted : ""
                    }`}
                    onClick={sortable ? () => toggleSort(c) : undefined}
                    aria-sort={
                      isSorted
                        ? sort?.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : sortable
                          ? "none"
                          : undefined
                    }
                  >
                    <span className={styles.thInner}>
                      <span className={styles.thLabel}>{c.header}</span>
                      {sortable && (
                        <span
                          className={`${styles.sortIcon} ${
                            isSorted ? styles.sortIconActive : ""
                          }`}
                          aria-hidden
                        >
                          {!isSorted && <ChevronsUpDown size={12} />}
                          {isSorted && sort?.dir === "asc" && <ArrowUp size={12} />}
                          {isSorted && sort?.dir === "desc" && <ArrowDown size={12} />}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td className={styles.emptyCell} colSpan={visibleColumns.length}>
                  {empty ?? "Sem registos."}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={rowKey(row)} className={styles.row}>
                  {visibleColumns.map((c) => (
                    <td key={c.key} className={styles.td}>
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ColumnsPickerProps<T> {
  columns: Column<T>[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onReset: () => void;
}

function ColumnsPicker<T>({ columns, hidden, onToggle, onReset }: ColumnsPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const hideable = columns.filter((c) => c.hideable !== false);
  const visibleCount = hideable.filter((c) => !hidden.has(c.key)).length;
  const totalCount = hideable.length;

  return (
    <div className={styles.picker} ref={ref}>
      <button
        type="button"
        className={`${styles.pickerButton} ${open ? styles.pickerButtonOpen : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Columns3 size={14} />
        <span>Colunas</span>
        <span className={styles.pickerCount}>
          {visibleCount}/{totalCount}
        </span>
      </button>
      {open && (
        <div className={styles.pickerMenu} role="dialog" aria-label="Gerir colunas">
          <header className={styles.pickerHeader}>
            <span className={styles.pickerHeaderTitle}>Gerir colunas</span>
            <button
              type="button"
              className={styles.pickerReset}
              onClick={onReset}
              disabled={hidden.size === 0}
              aria-label="Repor colunas"
            >
              <RotateCcw size={12} />
              Repor
            </button>
          </header>
          <ul className={styles.pickerList}>
            {hideable.map((col) => {
              const visible = !hidden.has(col.key);
              return (
                <li key={col.key} className={styles.pickerItem}>
                  <label className={styles.pickerLabel}>
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={() => onToggle(col.key)}
                      className={styles.pickerCheckbox}
                    />
                    <span className={styles.pickerName}>{columnLabel(col)}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
